import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Flame, HelpCircle, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatLevel } from "@/lib/skillLevels";
import type { PlayerProfile } from "@shared/schema";

interface HomeHeroStatsProps {
  playerProfile: PlayerProfile | null;
  matchHistory: Array<{ userWon: boolean }>;
  onInfoClick?: () => void;
  onCardClick?: () => void;
}

export function HomeHeroStats({ playerProfile, matchHistory, onInfoClick, onCardClick }: HomeHeroStatsProps) {
  // Calculate current streak
  let currentStreak = 0;
  let isCurrentWin = false;
  if (matchHistory.length > 0) {
    isCurrentWin = matchHistory[0].userWon;
    currentStreak = 1;
    for (let i = 1; i < matchHistory.length; i++) {
      if (matchHistory[i].userWon === isCurrentWin) {
        currentStreak++;
      } else {
        break;
      }
    }
  }

  // Calculate win rate
  const wins = matchHistory.filter(m => m.userWon).length;
  const winRate = matchHistory.length > 0 ? Math.round((wins / matchHistory.length) * 100) : 0;

  // Determine streak type and color
  const hasActiveStreak = currentStreak > 0;
  const streakType = isCurrentWin ? "victorias" : "derrotas";
  const streakColor = isCurrentWin 
    ? "from-victory to-victory/80" 
    : "from-defeat to-defeat/80";
  const streakIconColor = isCurrentWin 
    ? "text-victory" 
    : "text-defeat";

  // Calculate progress to next level (0.1 increments)
  const skillLevel = playerProfile?.skillLevel || 1.0;
  
  // Use the same logic as profile to ensure consistency
  // Note: We need levelHistory here to be 100% accurate if the profile is stale
  // but for now let's ensure we use the same level variable for calculations
  
  const currentLevelFloor = Math.floor(skillLevel * 10) / 10;
  const nextLevel = currentLevelFloor + 0.1;
  const progressToNext = ((skillLevel - currentLevelFloor) / 0.1) * 100;

  // Calculate trend line based on recent match results and win rate direction
  const recentMatches = matchHistory.slice(0, 5);
  const recentWins = recentMatches.filter(m => m.userWon).length;
  const recentLosses = recentMatches.length - recentWins;
  
  // Determine trend: if last match was a win and more wins than losses in recent matches, trending up
  const lastMatchWon = matchHistory.length > 0 ? matchHistory[0].userWon : false;
  const isUpTrend = lastMatchWon && recentWins > recentLosses;
  // If last match was a loss and more losses than wins in recent matches, trending down
  const isDownTrend = !lastMatchWon && matchHistory.length > 0 && recentLosses > recentWins;
  
  const trendLabel = isUpTrend ? "Subiendo" : isDownTrend ? "Bajando" : "Estable";
  const trendColor = isUpTrend ? "text-victory" : isDownTrend ? "text-defeat" : "text-muted-foreground";

  return (
    <div className="space-y-6">
      {/* Main Hero Card */}
      <Card 
        className={cn(
          "overflow-hidden border-2 border-primary/30 transition-all",
          onCardClick && "cursor-pointer hover-elevate"
        )}
        onClick={onCardClick}
        data-testid="card-hero-stats"
      >
        <div className="relative bg-gradient-to-br from-primary/10 via-background to-accent/5 p-6 lg:p-10">
          {/* Background accent circles */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -mr-24 -mt-24"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/5 rounded-full blur-3xl -ml-16 -mb-16"></div>

          <div className="relative z-10 space-y-6">
            {/* Primary Skill Level */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    Tu Nivel Global
                  </p>
                  {onInfoClick && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 -ml-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onInfoClick();
                      }}
                      data-testid="button-how-it-works"
                    >
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
                {onCardClick && (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-8xl lg:text-9xl font-black text-primary leading-none">
                  {formatLevel(skillLevel)}
                </div>
                <div className="flex flex-col items-center gap-1 -translate-y-2 lg:-translate-y-3">
                  {isUpTrend && <TrendingUp className="h-6 w-6 text-victory" />}
                  {isDownTrend && <TrendingDown className="h-6 w-6 text-defeat" />}
                  {!isUpTrend && !isDownTrend && (
                    <div className="h-6 w-6 flex items-center justify-center">
                      <div className="h-1 w-4 bg-muted-foreground rounded-full"></div>
                    </div>
                  )}
                  <span className={`text-xs font-semibold uppercase tracking-wider ${trendColor}`}>
                    {trendLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              {/* Ranking */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Ranking Nacional
                </p>
                <p className="text-3xl font-bold text-foreground tabular-nums">
                  {playerProfile?.nationalRank != null ? `#${playerProfile.nationalRank}` : "-"}
                </p>
                {playerProfile?.country && playerProfile.nationalRank == null && (
                  <p className="text-[10px] text-primary/70 mt-0.5 leading-snug">
                    Registra tu primer partido
                  </p>
                )}
              </div>

              {/* Win Rate */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Tasa de Victoria
                </p>
                <p className="text-3xl font-bold text-foreground tabular-nums">
                  {winRate}%
                </p>
              </div>

              {/* Matches Played */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Partidos Jugados
                </p>
                <p className="text-3xl font-bold text-foreground tabular-nums">
                  {playerProfile?.matchesPlayed || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Streak/Momentum Section */}
      <Card className={cn(
        "overflow-hidden border-2 transition-all duration-300",
        matchHistory.length > 0 && (hasActiveStreak && isCurrentWin 
          ? "border-victory/50 bg-gradient-to-br from-victory/10 to-victory/5" 
          : hasActiveStreak && !isCurrentWin
          ? "border-defeat/50 bg-gradient-to-br from-defeat/10 to-defeat/5"
          : "border-muted/30")
        || "border-muted/30"
      )}>
        <div className="p-6">
          {matchHistory.length === 0 ? (
            <div className="text-center space-y-3">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Tu Racha Actual
              </p>
              <p className="text-5xl font-black text-muted-foreground/40 leading-none">
                0
              </p>
              <p className="text-sm text-muted-foreground">
                Registra tu primer partido para empezar a rastrear tu racha
              </p>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-6">
              <div className="space-y-2 flex-1">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Tu Racha Actual
                </p>
                <div className="flex items-baseline gap-3">
                  <p className={cn(
                    "text-6xl font-black leading-none",
                    isCurrentWin ? "text-victory" : "text-defeat"
                  )}>
                    {currentStreak}
                  </p>
                  <div className="space-y-1">
                    <p className={cn(
                      "text-sm font-semibold capitalize",
                      isCurrentWin ? "text-victory" : "text-defeat"
                    )}>
                      {streakType}
                    </p>
                    {currentStreak >= 3 && isCurrentWin && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Flame className="h-3 w-3 text-achievement" />
                        ¡En fuego!
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Streak Icon */}
              <div className={cn(
                "p-4 rounded-xl",
                isCurrentWin 
                  ? "bg-victory/20" 
                  : "bg-defeat/20"
              )}>
                {isCurrentWin ? (
                  <TrendingUp className={cn("h-8 w-8", streakIconColor)} />
                ) : (
                  <TrendingDown className={cn("h-8 w-8", streakIconColor)} />
                )}
              </div>
            </div>
          )}

          {/* Achievement hint */}
          {matchHistory.length > 0 && currentStreak >= 5 && isCurrentWin && (
            <div className="mt-4 pt-4 border-t border-victory/30">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-achievement">✨ Consejo:</span> Continúa ganando para desbloquear logros de racha
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
